import React, { useState } from "react";
import { Box, Button, Container, Typography, Alert, CircularProgress } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getSurveyQuestions } from "src/constants/surveyQuestions";
import { PointDistribution } from "src/components/shared/PointDistribution";
import { MultipleChoice } from "src/components/shared/MultipleChoice";
import { SortingQuestion } from "src/components/shared/SortingQuestion";
import { AppDispatch, RootState } from "src/store";
import { updateCompanyCulture, mapSurveyResponsesToCulture } from "src/features/authSlice";

type CompanyCultureFormProps = {
  language?: string;
  onLanguageChange?: () => void;
};

export const CompanyCultureForm: React.FC<CompanyCultureFormProps> = ({ language = "tr" }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, loading, error } = useSelector((state: RootState) => state.auth);
  const [responses, setResponses] = useState<any>({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const companyUser = user?.accountType === "company" ? user : null;
  const surveyQuestions = getSurveyQuestions(language);

  const handleResponseChange = (id: number, value: any) => {
    setResponses((prev: any) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyUser?.companyId) {
      console.error("No company ID found");
      return;
    }

    // Debug: Log responses to see the format
    console.log("Survey Responses:", responses);
    console.log("Responses keys:", Object.keys(responses));

    // Map survey responses to backend culture format
    const cultureData = mapSurveyResponsesToCulture(responses);

    try {
      const result = await dispatch(
        updateCompanyCulture({
          companyId: companyUser.companyId,
          culture: cultureData,
        })
      );

      if (updateCompanyCulture.fulfilled.match(result)) {
        setSubmitSuccess(true);
        setTimeout(() => {
          navigate("/settings?tab=culture");
        }, 2000);
      }
    } catch (err) {
      console.error("Failed to submit culture data:", err);
    }
  };

  return (
    <Container maxWidth="md" sx={{ p: 0 }}>
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            padding: 2,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h5" mb={3} gutterBottom>
            Company Culture Survey
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Company culture data submitted successfully! Redirecting...
            </Alert>
          )}

          {surveyQuestions.map((question) => {
            if (question.type === "point-distribution") {
              return (
                <Box
                  key={question.id}
                  sx={{
                    marginBottom: 10,
                    boxShadow: 1,
                    padding: 4,
                    borderRadius: 2,
                  }}
                >
                  <PointDistribution
                    question={question.question}
                    options={question.options}
                    points={
                      responses[question.id] ||
                      question.options.map((option) => ({
                        id: option.id,
                        value: 0,
                      }))
                    }
                    setPoints={(points) =>
                      handleResponseChange(question.id, points)
                    }
                  />
                </Box>
              );
            }

            if (question.type === "multiple-choice") {
              return (
                <Box
                  key={question.id}
                  sx={{
                    marginBottom: 10,
                    boxShadow: 1,
                    padding: 4,
                    borderRadius: 2,
                  }}
                >
                  <MultipleChoice
                    question={question.question}
                    options={question.options}
                    onSubmit={(selectedOptions) =>
                      handleResponseChange(question.id, selectedOptions)
                    }
                  />
                </Box>
              );
            }

            if (question.type === "ranking") {
              return (
                <Box
                  key={question.id}
                  sx={{
                    marginBottom: 10,
                    boxShadow: 1,
                    padding: 4,
                    borderRadius: 2,
                  }}
                >
                  <SortingQuestion
                    question={question.question}
                    sourceItems={question.options}
                    onSubmit={(sortedOptions) =>
                      handleResponseChange(question.id, sortedOptions)
                    }
                  />
                </Box>
              );
            }

            return null;
          })}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || submitSuccess}
            sx={{ mt: 3, alignSelf: "flex-end", width: "200px" }}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? "Submitting..." : submitSuccess ? "Submitted!" : "Submit"}
          </Button>
        </Box>
      </form>
    </Container>
  );
};
